import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  timeout?: number
}

export const useUIStore = defineStore('ui', () => {
  // State
  const sidebarOpen = ref(true)
  const theme = ref<'light' | 'dark'>(
    (localStorage.getItem('theme') as 'light' | 'dark') || 'light'
  )
  const notifications = ref<Notification[]>([])
  const sessionExpired = ref(false)

  // Watch theme changes
  watch(theme, (newTheme) => {
    localStorage.setItem('theme', newTheme)
    document.documentElement.setAttribute('data-theme', newTheme)
  }, { immediate: true })

  // Actions
  function toggleSidebar(): void {
    sidebarOpen.value = !sidebarOpen.value
  }

  function setSidebarOpen(open: boolean): void {
    sidebarOpen.value = open
  }

  function toggleTheme(): void {
    theme.value = theme.value === 'light' ? 'dark' : 'light'
  }

  function setTheme(newTheme: 'light' | 'dark'): void {
    theme.value = newTheme
  }

  function addNotification(
    type: Notification['type'],
    message: string,
    timeout: number = 5000
  ): string {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    notifications.value.push({ id, type, message, timeout })

    if (timeout > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, timeout)
    }

    return id
  }

  function removeNotification(id: string): void {
    const index = notifications.value.findIndex((n) => n.id === id)
    if (index !== -1) {
      notifications.value.splice(index, 1)
    }
  }

  function clearNotifications(): void {
    notifications.value = []
  }

  // Convenience methods
  function success(message: string, timeout?: number): string {
    return addNotification('success', message, timeout)
  }

  function error(message: string, timeout?: number): string {
    return addNotification('error', message, timeout || 0) // Errors don't auto-dismiss by default
  }

  function warning(message: string, timeout?: number): string {
    return addNotification('warning', message, timeout)
  }

  function info(message: string, timeout?: number): string {
    return addNotification('info', message, timeout)
  }

  // Session timeout
  function showSessionTimeout(): void {
    sessionExpired.value = true
  }

  function clearSessionExpired(): void {
    sessionExpired.value = false
  }

  return {
    // State
    sidebarOpen,
    theme,
    notifications,
    sessionExpired,

    // Actions
    toggleSidebar,
    setSidebarOpen,
    toggleTheme,
    setTheme,
    addNotification,
    removeNotification,
    clearNotifications,
    success,
    error,
    warning,
    info,
    showSessionTimeout,
    clearSessionExpired
  }
})
